// imports
import React, { useState, useEffect, useCallback, useRef } from 'react';
import MyButton from 'components/ui/Button/MyButton';
import { Input, Card, Spin, Checkbox, Tooltip, Form, Select, Drawer } from 'antd'
import { PlusOutlined, DeleteOutlined, PlusCircleOutlined, EyeInvisibleOutlined, EyeOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import ToastMsg from "components/common/ToastMsg";
import { useNavigate, useLocation } from 'react-router-dom';

// static imports
import { SALARY_TEMPLATE } from "constants";
import { math } from '@amcharts/amcharts5';
import { ImagePaths } from "utils/ImagePath";
import IconButton from "components/ui/Button/IconButton";

// functions import
import { getEmployeeDesignation } from "hooks/api/masterApi/employeeDesignationApi";
import { getEmployeeAllDepartment } from "hooks/api/masterApi/employeeDepartmentApi";
import { salaryComponentType } from 'components/common/enum.js';
import { getSalaryComponentList } from "hooks/api/configurationApi/payrollPeriodApi";
import { noBlankSpacesValidator, areValuesNotEmptyExceptKeys, handleOnKeyDecimal, handleOnKeyNumeric, numberValidation } from 'components/common/validation';
import { CommonCancelPopup, CommonDeletePopup } from "components/common/CommonComponent";
import { default_total_earnings, default_total_deduction } from "components/common/enum";

// styles
import "../configuration.css";

// default states
let initialFormData = {
    id: '',
    is_active: true,
    code: "",
    salary_template_name: "",
    salary_department: "",
    salary_designation: "",
    monthly_ctc: 0,
    annual_ctc: 0,
    earning_components: [],
    total_earning: { ...default_total_earnings },
    deduction_components: [],
    total_deduction: { ...default_total_deduction },
    net_salary: 0,

    // add new components
    component_type: "",
    components: [],
    is_edit: false,
    is_draft: false,
    edit_component: []
}

let initalDropdownList = {
    designationList: [],
    departmentlist: [],
}

const CustomDropdown = ({
    label = "Select",
    list = [],
    displayKey = "name",
    selectedItem,
    setSelectedItem,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [hoveringIcon, setHoveringIcon] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const dropdownRef = useRef(null);
    const listRef = useRef(null);

    const filteredList = list.filter((item) =>
        item[displayKey].toLowerCase().includes(search.toLowerCase())
    );

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev < filteredList.length - 1 ? prev + 1 : 0
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) =>
                prev > 0 ? prev - 1 : filteredList.length - 1
            );
        } else if (e.key === "Enter" && highlightedIndex >= 0) {
            e.preventDefault();
            setSelectedItem(filteredList[highlightedIndex]);
            setIsOpen(false);
            setHighlightedIndex(-1);
        }
    };

    // Scroll selected into view
    useEffect(() => {
        if (listRef.current && highlightedIndex !== -1) {
            const el = listRef.current.children[highlightedIndex];
            if (el) el.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex]);

    return (
        <div ref={dropdownRef} className="relative w-full">
            <label className="block text-[12px] font-semibold text-gray-700 mb-1">
                {label}
            </label>

            {/* Trigger */}
            <div
                onClick={() => setIsOpen(!isOpen)}
                onKeyDown={handleKeyDown}
                tabIndex={0}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-[12px] text-gray-700 bg-white shadow-sm cursor-pointer flex justify-between items-center focus:ring-1 focus:ring-blue-300 transition-all duration-200"
            >
                <span className={`text-[12px] ${selectedItem ? "text-black" : "text-gray-400"}`}>
                    {selectedItem ? selectedItem[displayKey] : `Select ${label.toLowerCase()}`}
                </span>

                {/* Hover-based icon change */}
                <div
                    onMouseEnter={() => setHoveringIcon(true)}
                    onMouseLeave={() => setHoveringIcon(false)}
                    onClick={(e) => {
                        if (hoveringIcon && selectedItem) {
                            e.stopPropagation();
                            setSelectedItem(null);
                            setSearch("");
                            setIsOpen(false);
                        }
                    }}
                    className="ml-2"
                >
                    {hoveringIcon && selectedItem ? (
                        <DeleteOutlined />
                    ) : (
                        <svg
                            className={`w-4 h-4 transform transition-transform duration-200 ${isOpen ? "rotate-180" : "rotate-0"}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                    )}
                </div>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-md">
                    {/* Search input */}
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setHighlightedIndex(0);
                        }}
                        placeholder="Search..."
                        className="w-full px-3 py-2 border-b rounded-t-2xl border-gray-200 text-[12px] focus:outline-none"
                    />
                    <ul ref={listRef} className="max-h-60 overflow-y-auto">
                        {filteredList.length === 0 ? (
                            <li className="px-4 py-2 text-[12px] text-gray-400">No options found</li>
                        ) : (
                            filteredList.map((item, index) => (
                                <li
                                    key={index}
                                    onClick={() => {
                                        setSelectedItem(item);
                                        setIsOpen(false);
                                        setSearch("");
                                        setHighlightedIndex(-1);
                                    }}
                                    className={`px-4 py-2 text-[12px] cursor-pointer transition-all
                                        ${index === highlightedIndex ? "bg-blue-100" : "hover:bg-gray-100"}`}
                                >
                                    {item[displayKey]}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

const ComponentTable = ({ components, setComponents, type = "Earnings", monthlyCtc, componentList = [], earningComponents }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [newComponent, setNewComponent] = useState({
        name: "",
        percentage: "",
        calcof: "CTC",
        amount: null,
        is_employee_share: null,
        is_employer_share: null,
        employee_share_percentage: type === "Deductions" ? 0 : undefined,
        employer_share_percentage: type === "Deductions" ? 0 : undefined
    });
    const [visibleAmounts, setVisibleAmounts] = useState({});
    const customConfigLocal = JSON.parse(sessionStorage.getItem("customConfig"))
    const pfLimit = customConfigLocal?.pf_limit || 15000;
    const capAmount = customConfigLocal?.cap_amount || 0;
    const ESCILimitUpto = customConfigLocal?.esic_limit_upto || 21000;

    // console.log(pfLimit, capAmount, ESCILimitUpto); // Debugging purpose: 15000, 1800, 21000

    const toggleVisibility = (index) => {
        setVisibleAmounts((prev) => ({
            ...prev,
            [index]: !prev[index],
        }));
    };


    const handleChange = (index, key, value) => {
        const updated = [...components];
        updated[index][key] = value;
        setComponents(updated);
    };

    const handleDelete = (index) => {
        const updated = components.filter((_, i) => i !== index);
        setComponents(updated);
    };

    const handleAdd = () => {
        setIsDrawerOpen(true);
    };

    // Calculate amounts based on percentage and monthly CTC
    const calculateAmounts = (component) => {
        if (type === "Earnings") {
            const percentage = parseFloat(component.percentage) || 0;

            let baseAmount = 0;

            if (component.calcof === "CTC") {
                baseAmount = monthlyCtc;
            } else {
                // find the reference earning component
                const refComponent = earningComponents.find(c => c.name === component.calcof);
                if (refComponent) {
                    // if refComponent already has monthly_amount calculated, use that
                    baseAmount = parseFloat(refComponent.monthly_amount) || 0;

                    // fallback → recalc from percentage if not yet calculated
                    if (!baseAmount) {
                        const refPercentage = parseFloat(refComponent.percentage) || 0;
                        baseAmount = (monthlyCtc * refPercentage) / 100;
                    }
                }
            }

            const monthly = (baseAmount * percentage) / 100;

            return {
                monthly: monthly.toFixed(2),
                annual: (monthly * 12).toFixed(2)
            };
        } else {
            // Deductions
            const basicPercentage = parseFloat(
                earningComponents.find(c => c.name === "Basic")?.percentage
            ) || 0;
            const basicAmount = (monthlyCtc * basicPercentage) / 100;

            const baseAmount = component.calcof === "Basic" ? basicAmount : monthlyCtc;

            const empPercent = parseFloat(component.employee_share_percentage) || 0;
            const employerPercent = parseFloat(component.employer_share_percentage) || 0;

            let employeeMonthly = 0;
            let employerMonthly = 0;

            // PF limit handling
            if (component.name.toLowerCase() === "pf") {
                // PF amount is based on basic amount but capped
                const pfBase = Math.min(baseAmount, pfLimit);
                employeeMonthly = component.is_employee_share ? (pfBase * empPercent) / 100 : 0;
                employerMonthly = component.is_employer_share ? (pfBase * employerPercent) / 100 : 0;

                // If capped monthly amount is set, override
                if (capAmount > 0 && pfBase === pfLimit) {
                    employeeMonthly = component.is_employee_share ? capAmount : 0;
                    employerMonthly = component.is_employer_share ? capAmount : 0;
                }

                // ESIC limit handling
            } else if (component.name.toLowerCase() === "esic") {
                if (monthlyCtc <= ESCILimitUpto) {
                    employeeMonthly = component.is_employee_share ? (baseAmount * empPercent) / 100 : 0;
                    employerMonthly = component.is_employer_share ? (baseAmount * employerPercent) / 100 : 0;
                } else {
                    // Above limit → ESIC is zero
                    employeeMonthly = 0;
                    employerMonthly = 0;
                }

                // Default deductions
            } else {
                employeeMonthly = component.is_employee_share ? (baseAmount * empPercent) / 100 : 0;
                employerMonthly = component.is_employer_share ? (baseAmount * employerPercent) / 100 : 0;
            }

            let totalMonthly;
            if (!component.is_employee_share && !component.is_employer_share) {
                totalMonthly = parseFloat(component.monthly_amount) || 0;
            } else {
                totalMonthly = employeeMonthly + employerMonthly;
            }

            return {
                monthly: totalMonthly.toFixed(2),
                annual: (totalMonthly * 12).toFixed(2),
                employeeMonthly: employeeMonthly.toFixed(2),
                employerMonthly: employerMonthly.toFixed(2)
            };
        }
    };

    // Calculate totals for earnings
    const totalEarningsPercentage = components?.reduce((acc, curr) => {
        return curr.calcof === "CTC" && type === "Earnings" ? acc + Number(curr.percentage || 0) : acc;
    }, 0);
    const totalEarningMonthly = components?.reduce((acc, curr) => {
        if (curr.calcof === "CTC") {
            return acc + (monthlyCtc * (curr.percentage || 0)) / 100;
        }
        return acc;
    }, 0);
    const totalEarningAnnual = totalEarningMonthly * 12;

    // Calculate totals for deductions
    const totalEmployeeDeductionsPercentage = components?.reduce((acc, curr) => {
        return type === "Deductions" ? acc + Number(curr.employee_share_percentage || 0) : acc;
    }, 0);
    const totalEmployerDeductionsPercentage = components?.reduce((acc, curr) => {
        return type === "Deductions" ? acc + Number(curr.employer_share_percentage || 0) : acc;
    }, 0);
    const totalDeductionMonthly = type === "Deductions"
        ? components.reduce((acc, component) => acc + Number(calculateAmounts(component).monthly), 0)
        : 0;

    const totalDeductionAnnual = totalDeductionMonthly * 12;

    // Handle adding a new component
    const handleAddComponent = () => {
        if (!newComponent.name) return;

        const componentToAdd = {
            id: newComponent.id,
            name: newComponent.name,
            calcof: newComponent.calcof,
            ...(type === "Earnings" ? {
                percentage: parseFloat(newComponent.percentage),
                annual_amount: 0,
                monthly_amount: 0
            } : {
                amount: newComponent.amount,
                is_employee_share: newComponent.is_employee_share,
                is_employer_share: newComponent.is_employer_share,
                employee_share_percentage: parseFloat(newComponent.employee_share_percentage) || 0,
                employer_share_percentage: parseFloat(newComponent.employer_share_percentage) || 0,
                employee_share_monthly_amount: 0,
                employer_share_monthly_amount: 0,
                percentage: parseFloat(newComponent.employee_share_percentage) +
                    parseFloat(newComponent.employer_share_percentage) || 0
            })
        };

        setComponents(prev => [...prev, componentToAdd]);
        setIsDrawerOpen(false);
        setNewComponent({
            name: "",
            percentage: "",
            calcof: "CTC",
            amount: type === "Deductions",
            is_employee_share: type === "Deductions",
            is_employer_share: type === "Deductions",
            employee_share_percentage: type === "Deductions" ? 0 : undefined,
            employer_share_percentage: type === "Deductions" ? 0 : undefined
        });
    };

    const hasBasicComponent = components.some(item => item.name?.toLowerCase() === "basic");
    const isTotalCTCPercentValid = totalEarningsPercentage?.toFixed(2) === "100.00";

    return (
        <div>
            <h2 className="text-[15px] font-semibold text-gray-800 mb-1">{type}</h2>
            <p className="text-[12px] text-gray-500 mb-2">Define the {type.toLowerCase()} components of the salary.</p>

            {type == 'Earnings' && (!hasBasicComponent || !isTotalCTCPercentValid) && (
                <div className="py-2 text-red-400 text-[10px]">
                    {!hasBasicComponent && (
                        <div>⚠️ A <strong>Basic</strong> earning component must exist.</div>
                    )}
                    {!isTotalCTCPercentValid && (
                        <div>⚠️ Total earnings percentage calculated from <strong>CTC</strong> must be exactly <strong>100%</strong>.</div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-12 text-[12px] font-medium text-gray-600 border-b mt-1 pb-2">
                <div className="col-span-2">Component Name</div>
                <div className="col-span-1">Of</div>
                <div className="col-span-2">Percentage</div>
                <div className="col-span-3 text-right">Monthly Amount</div>
                <div className="col-span-3 text-right">Annual Amount</div>
                <div className="col-span-1 text-center"> </div>
            </div>

            {components?.map((item, index) => {
                const amounts = calculateAmounts(item);

                return (
                    <div
                        key={index}
                        className={
                            `grid grid-cols-12 gap-2 text-[12px] items-center py-3 hover:bg-gray-50 border-b last:border-b-0
                            ${item.calcof === "Basic" && type !== "Deductions" ? "bg-gray-200 text-white" : ""}`
                        }
                    >
                        <div className="col-span-2">
                            <span className='text-gray-700'>{item.name}</span>
                        </div>
                        <div className="col-span-1">
                            <span className={`inline-block px-2 py-1 rounded-full font-medium text-[11px] 
                    ${item.calcof === "CTC" ? "bg-gray-100 text-gray-700" :
                                    item.calcof === "Basic" ? "bg-green-100 text-green-700" :
                                        "bg-gray-100 text-gray-700"}`}>
                                {item.calcof}
                            </span>
                        </div>

                        {type === "Deductions" ? (
                            <>
                                {
                                    !item.is_employee_share && !item.is_employer_share ? <div className='col-span-2'>Fixed Amount</div> :
                                        <>
                                            <div className="col-span-1">
                                                <Tooltip title="Employee Share %">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter %"
                                                        value={item.employee_share_percentage}
                                                        onChange={(e) => {
                                                            let val = Number(e.target.value);
                                                            if (val > 100) val = 100;
                                                            handleChange(index, "employee_share_percentage", val)
                                                        }}
                                                        disabled={!item.is_employee_share}
                                                        className={`w-full px-2 py-1 rounded-md border text-gray-700 ${!item.is_employee_share ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                                                            }`}
                                                    />
                                                </Tooltip>
                                            </div>
                                            <div className="col-span-1">
                                                <Tooltip title="Employer Share %">
                                                    <input
                                                        type="number"
                                                        placeholder="Enter %"
                                                        value={item.employer_share_percentage}
                                                        onChange={(e) => {
                                                            let val = Number(e.target.value);
                                                            if (val > 100) val = 100;
                                                            handleChange(index, "employer_share_percentage", val)
                                                        }}
                                                        disabled={!item.is_employer_share}
                                                        className={`w-full px-2 py-1 rounded-md border text-gray-700 ${!item.is_employer_share ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                                                            }`}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </>
                                }
                            </>
                        ) : (
                            <div className="col-span-2">
                                <input
                                    type="number"
                                    placeholder="Enter %"
                                    value={item.percentage}
                                    onChange={(e) => handleChange(index, "percentage", e.target.value)}
                                    className="w-full px-2 py-1 rounded-md bg-white border border-gray-300 text-gray-700"
                                />
                            </div>
                        )}

                        <div className="col-span-3 text-right text-gray-700 font-medium flex items-center justify-end gap-2">
                            {(type === 'Deductions' && !item.is_employee_share && !item.is_employer_share) ? (
                                <input
                                    type="number"
                                    placeholder="₹ Enter monthly amount"
                                    value={item.monthly_amount || ""}
                                    onChange={(e) => handleChange(index, "monthly_amount", e.target.value)}
                                    className="w-[80px] px-2 py-1 rounded-md bg-white border border-gray-300 text-gray-700 text-right"
                                />
                            ) : (
                                <span>
                                    {visibleAmounts[index] && item.is_employee_share && item.is_employer_share
                                        ? `₹${amounts.employeeMonthly} / ₹${amounts.employerMonthly}`
                                        : `₹${amounts.monthly}`
                                    }
                                </span>
                            )}

                            {item.is_employee_share && item.is_employer_share && (
                                <button onClick={() => toggleVisibility(index)}>
                                    {visibleAmounts[index] ? (
                                        <EyeInvisibleOutlined className="text-gray-500 hover:text-black" />
                                    ) : (
                                        <EyeOutlined className="text-gray-500 hover:text-black" />
                                    )}
                                </button>
                            )}
                        </div>

                        <div className="col-span-3 text-right text-gray-700 font-medium flex items-center justify-end gap-2">
                            <span>
                                {visibleAmounts[index] && item.is_employee_share && item.is_employer_share
                                    ? `₹${amounts.employeeMonthly * 12} / ₹${amounts.employerMonthly * 12}`
                                    : `₹${amounts.annual}`
                                }
                            </span>
                            {item.is_employee_share && item.is_employer_share && (
                                <button onClick={() => toggleVisibility(index)}>
                                    {visibleAmounts[index] ? (
                                        <EyeInvisibleOutlined className="text-gray-500 hover:text-black" />
                                    ) : (
                                        <EyeOutlined className="text-gray-500 hover:text-black" />
                                    )}
                                </button>
                            )}
                        </div>
                        <div className="col-span-1 flex justify-center">
                            <button onClick={() => handleDelete(index)}>
                                <DeleteOutlined className="w-4 h-4 text-red-500" />
                            </button>
                        </div>
                    </div>
                );
            })}

            <button
                onClick={handleAdd}
                className="mt-3 flex items-center gap-1 text-[12px] font-medium hover:text-gray-600"
            >
                <PlusCircleOutlined className="w-4 h-4" />
                Add {type.slice(0, -1)}
            </button>

            {/* drawer */}
            <Drawer
                title={`Add ${type.slice(0, -1)} Component`}
                placement="right"
                width={400}
                onClose={() => setIsDrawerOpen(false)}
                open={isDrawerOpen}
            >
                <div className="flex flex-col gap-4">
                    <CustomDropdown
                        label="Component Name"
                        list={componentList}
                        displayKey="salary_component_name"
                        selectedItem={newComponent.name ?
                            { salary_component_name: newComponent.name } :
                            null
                        }
                        setSelectedItem={(value) =>
                            setNewComponent({
                                ...newComponent,
                                id: value.id,
                                name: value.salary_component_name,
                                amount: value.amount,
                                is_employee_share: value.employee_share,
                                is_employer_share: value.employer_share
                            })
                        }
                    />

                    <CustomDropdown
                        label="Calcuate Of"
                        list={[{ name: "CTC" }, { name: "Basic" }]}
                        selectedItem={{ name: newComponent.calcof }}
                        setSelectedItem={(val) => setNewComponent({ ...newComponent, calcof: val.name })}
                    />

                    {type === "Earnings" && (
                        <div>
                            <label className="block text-[11px] font-medium text-gray-600 mb-1">
                                Percentage
                            </label>
                            <input
                                type="number"
                                placeholder='Enter percentage'
                                value={newComponent.percentage}
                                onChange={(e) => setNewComponent({ ...newComponent, percentage: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-[12px] focus:ring-1 focus:ring-blue-300 outline-none"
                            />
                        </div>
                    )}

                    <button
                        onClick={handleAddComponent}
                        className="bg-[#04B7B1] text-white py-2 px-4 rounded hover:bg-[#04B7B1]/80 mt-4"
                    >
                        Add Component
                    </button>
                </div>
            </Drawer>

            <div className="mt-4 border-t pt-3 flex justify-end text-[12px] text-gray-700 font-medium">
                <div className="space-x-6 flex items-center">
                    {
                        type === 'Earnings' ? (
                            <span>
                                Total % (of CTC):{" "}
                                <span
                                    className={`font-semibold ${type === 'Earnings' && totalEarningsPercentage?.toFixed(2) !== "100.00"
                                        ? "text-red-600"
                                        : "text-gray-700"
                                        }`}
                                >
                                    {totalEarningsPercentage?.toFixed(2)}%
                                </span>
                            </span>
                        ) : (
                            <span className=' flex gap-3'>
                                <span>
                                    Total (Employee Share) %:{" "}{totalEmployeeDeductionsPercentage?.toFixed(2)}%
                                </span>
                                <span>
                                    Total (Employer Share) %:{" "}{totalEmployerDeductionsPercentage?.toFixed(2)}%
                                </span>
                            </span>
                        )
                    }
                    {
                        type === 'Earnings' ? (
                            <span>Total Monthly: <strong>₹{totalEarningMonthly?.toFixed(2)}</strong></span>
                        ) : (
                            <span>Total Monthly: <strong>₹{totalDeductionMonthly?.toFixed(2)}</strong></span>
                        )
                    }
                    {
                        type === 'Earnings' ? (
                            <span>Total Annual: <strong>₹{totalEarningAnnual?.toFixed(2)}</strong></span>
                        ) : (
                            <span>Total Annual: <strong>₹{totalDeductionAnnual?.toFixed(2)}</strong></span>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

const NewAddEditSalaryTemplate = () => {

    //  states
    const [formData, setFormData] = useState(initialFormData);
    const [dropdownList, setDropDownList] = useState(initalDropdownList);

    const [loadings, setLoadings] = useState(false);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);
    const [earningComponents, setEarningComponents] = useState([]);
    const [deductionComponents, setDeductionComponents] = useState([]);

    const [monthlyCTC, setMonthlyCTC] = useState();
    const [annualCTC, setAnnualCTC] = useState();

    const [basicPay, setBasicPay] = useState(0);
    const [totalEarnings, setTotalEarnings] = useState(0);
    const [totalDeductions, setTotalDeductions] = useState(0);
    const [totalEmployerDeductions, setTotalEmployerDeductions] = useState(0);

    const [netPayMonthly, setNetPayMonthly] = useState(0);
    const [netPayAnnual, setNetPayAnnual] = useState(0);

    const [isFormValid, setIsFormValid] = useState(false);
    const [isCancelClk, setIsCancelClk] = useState(false);

    // variables
    const navigate = useNavigate();
    const location = useLocation();
    const prevPropsRef = useRef();
    const { fromEdit, editDetails, isActive } = location.state || {};

    const formValid = () => {
        const { salary_template_name } = formData;

        // console.log("Checking form validity...", formData);

        const validEarnings = earningComponents.some(comp => comp.name?.trim(parseFloat(comp.percentage) > 0))
        const totalEarningsPercentage = earningComponents?.reduce((acc, curr) => {
            return curr.calcof === "CTC" ? acc + Number(curr.percentage || 0) : acc;
        }, 0);
        const hasBasicComponent = earningComponents.some(item => item.name?.toLowerCase() === "basic"); // true if basic exist
        const isTotalCTCPercentValid = totalEarningsPercentage?.toFixed(2) === "100.00"; // true is total earnings is not equal to 100

        if (!selectedDept) return true;
        if (!selectedDesig) return true;
        if (!validEarnings) return true
        if (!hasBasicComponent) return true;
        if (!isTotalCTCPercentValid) return true;
        if (!salary_template_name.trim()) return true;

        return false;
    };

    useEffect(() => {
        setIsFormValid(formValid());
    }, [formData.salary_template_name, formData.salary_department, formData.earning_components]);

    useEffect(() => {
        if (editDetails !== null &&
            Object.keys(editDetails).length !== 0 &&
            JSON.stringify(prevPropsRef.current) !== JSON.stringify(editDetails) &&
            JSON.stringify(formData) !== JSON.stringify(editDetails)) {
            let updateData = {
                is_active: editDetails?.is_active,
                code: editDetails?.code ? editDetails?.code : "",
                salary_template_name: editDetails?.salary_template_name ? editDetails?.salary_template_name : "",
                salary_department: editDetails?.department ? editDetails?.department?.id : "",
                salary_designation: editDetails?.designation ? editDetails?.designation?.id : "",
                monthly_ctc: editDetails?.monthly_ctc ? editDetails?.monthly_ctc : 0,
                annual_ctc: editDetails?.annual_ctc ? editDetails?.annual_ctc : 0,
                earning_components: editDetails?.earning_components ? editDetails?.earning_components : [],
                total_earning: { ...editDetails?.total_earning },
                deduction_components: editDetails?.deduction_components ? editDetails?.deduction_components : [],
                total_deduction: { ...editDetails?.total_deduction },
                net_salary: editDetails?.net_salary ? editDetails?.net_salary : 0,
                is_draft: editDetails?.is_draft ? editDetails?.is_draft : false,
                id: editDetails?.id ? editDetails?.id : 0,
            }

            setSelectedDept(editDetails?.department ? editDetails?.department : "")
            setSelectedDesig(editDetails?.designation ? editDetails?.designation : "")
            setEarningComponents(editDetails?.earning_components ? editDetails?.earning_components : [])
            setDeductionComponents(
                editDetails?.deduction_components?.map(comp => ({
                    ...comp,
                    monthly_amount: comp.is_amount ? comp.employee_share_monthly_amount : 0
                })) || []
            );
            setFormData((prevFormData) => ({
                ...prevFormData,
                id: editDetails.id,
                ...updateData
            }));
            prevPropsRef.current = editDetails;
        }
    }, [editDetails, formData])

    // functions
    const fetchDropdownData = async () => {
        setLoadings(true)
        try {
            const [designationRes, departmentRes, salaryComponentRes] = await Promise.all([
                getEmployeeDesignation(`drop_down=True`),
                getEmployeeAllDepartment(`drop_down=True`),
                getSalaryComponentList(`drop_down=True`)
            ]);

            const designationList = designationRes?.status === "success" ? designationRes.data : [];
            const departmentlist = departmentRes?.status === "success" ? departmentRes.data : [];

            const allComponents = salaryComponentRes?.status === "success" && Array.isArray(salaryComponentRes?.data)
                ? salaryComponentRes.data
                : [];

            const earningComponents = allComponents.filter(item => item.component_type === "earning");
            const deductionComponents = allComponents.filter(item => item.component_type === "deduction");

            setDropDownList({
                designationList,
                departmentlist,
                componentList: allComponents,
                earningComponents,
                deductionComponents,
            });

            setLoadings(false)

        } catch (error) {
            ToastMsg('error', "Error loading dropdowns");
            setDropDownList({
                designationList: [],
                departmentlist: [],
                componentList: [],
                earningComponents: [],
                deductionComponents: [],
            });
        }
        setLoadings(false)
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useEffect(() => {
        if (monthlyCTC > 0) {
            const breakdown = calculateSalaryBreakdown(monthlyCTC);
            setBasicPay(breakdown.basicPay);
            setTotalEarnings(breakdown.totalEarnings);
            setTotalDeductions(breakdown.totalDeductions);
            setTotalEmployerDeductions(breakdown.totalEmployerDeductions);
            setNetPayMonthly(breakdown.netPayMonthly);
            setNetPayAnnual(breakdown.netPayAnnual);
        }
    }, [earningComponents, deductionComponents, monthlyCTC]);

    // event handlers
    const preparePayload = (formData, earningComponents, deductionComponents, selectedDept, selectedDesig, isdraft = false) => {
        // Calculate amounts for earning components
        const updatedEarningComponents = earningComponents.map(comp => {
            return {
                id: comp.id,
                name: comp.name,
                calcof: comp.calcof || "CTC",
                percentage: parseFloat(comp.percentage),
                annual_amount: 0,
                monthly_amount: 0
            }
        });

        // Calculate amounts for deduction components
        const updatedDeductionComponents = deductionComponents.map(comp => {
            return {
                id: comp.id,
                name: comp.name,
                calcof: comp.calcof || "CTC",
                is_amount: comp.amount || false,
                annual_amount: 0,
                monthly_amount: 0,
                is_employee_share: comp.is_employee_share || false,
                is_employer_share: comp.is_employer_share || false,
                employee_share_percentage: parseFloat(comp.employee_share_percentage) || 0,
                employer_share_percentage: parseFloat(comp.employer_share_percentage) || 0,
                employee_share_monthly_amount: comp.amount === true ? Number(comp.monthly_amount) : 0,
                employer_share_monthly_amount: 0
            }
        });

        // Calculate totals
        const totalEarningPercentage = updatedEarningComponents?.reduce((sum, comp) => comp.calcof === "CTC" ? sum + Number(comp.percentage || 0) : sum, 0);
        const totalEarningMonthly = updatedEarningComponents.reduce((sum, comp) => sum + comp.monthly_amount, 0);

        const totalDeductionPercentage = updatedDeductionComponents?.reduce((sum, comp) => sum + Number(comp.employee_share_percentage || 0), 0);
        const totalDeductionMonthly = updatedDeductionComponents.reduce((sum, comp) => sum + comp.monthly_amount, 0);
        const totalEmployeeDeduction = updatedDeductionComponents.reduce((sum, comp) => sum + comp.employee_share_monthly_amount, 0);
        const totalEmployerDeduction = updatedDeductionComponents.reduce((sum, comp) => sum + comp.employer_share_monthly_amount, 0);

        return {
            id: formData.id,
            code: formData.code,
            is_active: formData.is_active,
            salary_template_name: formData.salary_template_name,
            monthly_ctc: 0,
            annual_ctc: 0,
            earning_components: updatedEarningComponents,
            total_earning: {
                annual_amount: 0,
                monthly_amount: 0,
                percentage: totalEarningPercentage
            },
            deduction_components: updatedDeductionComponents,
            total_deduction: {
                annual_amount: totalDeductionMonthly * 12,
                employee_monthly_amount: 0,
                employer_monthly_amount: 0,
                percentage: totalDeductionPercentage
            },
            net_salary: 0,
            department: selectedDept ? selectedDept.id : [],
            designation: selectedDesig ? selectedDesig.id : [],
            is_draft: isdraft
        };
    };

    // Calculate all salary components when monthly CTC changes
    const customConfigLocal = JSON.parse(sessionStorage.getItem("customConfig"));
    const pfLimit = customConfigLocal?.pf_limit || 15000;   // PF basic limit
    const capAmount = customConfigLocal?.cap_amount || 0;   // PF capped monthly amount (e.g., 1800)
    const ESCILimitUpto = customConfigLocal?.esic_limit_upto || 21000; // ESIC limit

    const calculateSalaryBreakdown = (ctc) => {
        // Step 1: Calculate earnings first
        const earnings = earningComponents.map(comp => {
            const monthly = (ctc * (comp.percentage || 0)) / 100;
            return {
                ...comp,
                monthly_amount: monthly,
                annual_amount: monthly * 12
            };
        });

        // Step 2: Extract base components like Basic, HRA etc., for deduction calcof
        const earningsMap = earnings.reduce((acc, curr) => {
            acc[curr.name.toLowerCase()] = curr;
            return acc;
        }, {});

        // Step 3: Calculate deductions with PF & ESIC validation
        const deductions = deductionComponents.map(comp => {
            const baseKey = (comp.calcof || '').toLowerCase(); // e.g., "basic"
            let baseAmount = 0;

            if (baseKey === 'ctc') {
                baseAmount = ctc;
            } else {
                const baseComponent = earningsMap[baseKey];
                baseAmount = baseComponent?.monthly_amount || 0;
            }

            let employeeShare = 0;
            let employerShare = 0;
            let monthlyTotal = 0;

            // Case 1: Manual amount
            if (!comp.is_employee_share && !comp.is_employer_share) {
                monthlyTotal = parseFloat(comp.monthly_amount) || 0;
            } else {
                // Apply PF rules
                if (comp.name.toLowerCase() === "pf") {
                    const pfBase = Math.min(baseAmount, pfLimit);

                    employeeShare = comp.is_employee_share ? (pfBase * (parseFloat(comp.employee_share_percentage) || 0)) / 100 : 0;
                    employerShare = comp.is_employer_share ? (pfBase * (parseFloat(comp.employer_share_percentage) || 0)) / 100 : 0;

                    // Cap amount override
                    if (capAmount > 0 && pfBase === pfLimit) {
                        employeeShare = comp.is_employee_share ? capAmount : 0;
                        employerShare = comp.is_employer_share ? capAmount : 0;
                    }

                    // Apply ESIC rules
                } else if (comp.name.toLowerCase() === "esic") {
                    if (ctc <= ESCILimitUpto) {
                        employeeShare = comp.is_employee_share ? (baseAmount * (parseFloat(comp.employee_share_percentage) || 0)) / 100 : 0;
                        employerShare = comp.is_employer_share ? (baseAmount * (parseFloat(comp.employer_share_percentage) || 0)) / 100 : 0;
                    } else {
                        employeeShare = 0;
                        employerShare = 0;
                    }

                    // Default deductions
                } else {
                    if (comp.is_employee_share) {
                        employeeShare = (baseAmount * (parseFloat(comp.employee_share_percentage) || 0)) / 100;
                    }
                    if (comp.is_employer_share) {
                        employerShare = (baseAmount * (parseFloat(comp.employer_share_percentage) || 0)) / 100;
                    }
                }

                monthlyTotal = employeeShare + employerShare;
            }

            return {
                ...comp,
                monthly_amount: monthlyTotal,
                annual_amount: monthlyTotal * 12,
                employee_share_monthly_amount: employeeShare,
                employer_share_monthly_amount: employerShare
            };
        });

        // Step 4: Totals
        const totalEarnings = earnings.reduce((sum, comp) => {
            if (comp.calcof === 'CTC') {
                return sum + Number(comp.monthly_amount || 0);
            }
            return sum;
        }, 0);
        const totalDeductions = deductions.reduce((sum, comp) => sum + comp.monthly_amount, 0);

        const totalEmployeeDeductions = deductions.reduce((sum, comp) => {
            const isFixedAmount =
                comp.is_employee_share === false &&
                comp.is_employer_share === false &&
                comp.amount === true;

            const employeeAmount = Number(comp.employee_share_monthly_amount || 0);
            const monthly = Number(comp.monthly_amount || 0);

            return sum + (isFixedAmount ? (employeeAmount + monthly) : employeeAmount);
        }, 0);

        const totalEmployerDeductions = deductions.reduce((sum, comp) => sum + comp.employer_share_monthly_amount, 0);

        const basicPayComp = earningsMap["basic"];
        const basicPay = basicPayComp ? basicPayComp.monthly_amount : 0;

        return {
            earnings,
            deductions,
            basicPay,
            totalEarnings,
            totalDeductions,
            totalEmployeeDeductions,
            totalEmployerDeductions,
            netPayMonthly: ctc - (totalEmployeeDeductions + totalEmployerDeductions),
            netPayAnnual: (ctc - (totalEmployeeDeductions + totalEmployerDeductions)) * 12
        };
    };

    // Handle monthly CTC change
    const handleMonthlyCTCChange = (value) => {
        const ctc = parseFloat(value) || 0;
        setMonthlyCTC(ctc);
        setAnnualCTC(ctc * 12);
        setFormData(prev => ({ ...prev, monthly_ctc: ctc, annual_ctc: ctc * 12 }));

        // Calculate all breakdowns
        const breakdown = calculateSalaryBreakdown(ctc);

        // Update state with calculated values
        setBasicPay(breakdown.basicPay);
        setTotalEarnings(breakdown.totalEarnings);
        setTotalDeductions(breakdown.totalDeductions);
        setNetPayMonthly(breakdown.netPayMonthly);
        setNetPayAnnual(breakdown.netPayAnnual);

        // Update components with calculated amounts
        setEarningComponents(breakdown.earnings);
        setDeductionComponents(breakdown.deductions);
    };

    // submit handler
    const handleSubmit = async (isdraft = false) => {
        try {
            let methods = fromEdit ? "PUT" : "POST";
            const authtoken = sessionStorage.getItem("token");
            setLoadings(true);

            // Prepare the payload
            const payload = preparePayload(
                formData,
                earningComponents,
                deductionComponents,
                selectedDept,
                selectedDesig,
                isdraft
            );

            // console.log(payload);

            // Add your API call here
            fetch(SALARY_TEMPLATE, {
                method: methods,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `token ${authtoken}`
                },
                body: JSON.stringify(payload)
            })
                .then((response) => response.json())
                .then(async data => {
                    if (data.status === "success") {
                        ToastMsg("success", data.message);
                        setLoadings(false)
                        setFormData(initialFormData)
                        navigate('/configuration/salarytemplate');
                    }
                    else if (data.status === "fail") {
                        setLoadings(false);
                        ToastMsg("error", data.message);
                    }
                })
                .catch(error => {
                    setLoadings(false);
                    ToastMsg("error", error.message);
                });
        } catch (error) {
            ToastMsg("error", error.message || "Failed to create salary template");
        } finally {
            setLoadings(false);
        }
    };

    const handleInitalCancelBtn = () => {
        if (formData.is_draft) {
            navigate('/configuration/salarytemplate');
        } else {
            if (!formData.salary_template_name && !formData.salary_designation && !formData.salary_department) {
                navigate('/configuration/salarytemplate');
            } else {
                setIsCancelClk(true);
            }
        }

    };

    const handleCancelBtn = () => {
        setIsCancelClk(false); // Just close the modal & navigate
        navigate('/configuration/salarytemplate');
    };

    const handleSaveAsDraft = () => {
        // console.log("Saving as draft...");
        // send the draft value as true
        handleSubmit(true)
    };


    // render functions

    // conditional rendering

    // return JSX
    return (
        <div>
            {loadings && (
                <div className="loaderOverlay">
                    <Spin />
                </div>
            )}
            <div className='flex justify-between items-center border-b bg-white rounded-md'>
                {/* header */}
                <div className="flex items-center cursor-pointer">
                    <Tooltip title={"Back"} placement="bottom">
                        <img src={ImagePaths.backArrow.default}
                            alt="Back" className='commonTblBackIcon'
                            onClick={handleCancelBtn}
                        />
                    </Tooltip>
                    <p className='runpayRoll_Txt py-1'>Add Salary Template</p>
                </div>
                <Checkbox
                    disabled={fromEdit ? !isActive : true}
                    checked={formData?.is_active}
                    onChange={(e) => {
                        setFormData((prev) => ({
                            ...prev,
                            is_active: e.target.checked
                        }));
                    }}
                    className="text-[12px] font-medium text-gray-700"
                >
                    Active
                </Checkbox>
            </div>
            <div className="grid grid-cols-[2fr_1fr] gap-2 mt-2">
                {/* Column 1: Three cards stacked vertically */}
                <div className="flex flex-col gap-2 overflow-y-scroll h-[calc(100vh-120px)]">
                    <Card className="p-2">
                        {/* Header */}
                        <h3 className="font-medium text-[15px] text-gray-800">Template Details</h3>
                        <span className="text-[12px] text-gray-500">
                            Set up the basic information for this salary template.
                        </span>

                        {/* Form Inputs */}
                        <div className="mt-4 grid grid-cols-1 gap-4">

                            {/* Template Name */}
                            <div>
                                <label className="block text-[12px] font-medium text-gray-600 mb-1">Template Name</label>
                                <input
                                    type="text"
                                    placeholder="Enter template name"
                                    value={formData.salary_template_name}
                                    onChange={(e) => setFormData({ ...formData, salary_template_name: e.target.value })}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-[12px] focus:outline-none focus:ring-1"
                                />
                            </div>

                            {/* Department and Designation in 2-column row */}
                            <div className="grid grid-cols-2 gap-4">
                                {/* Department */}
                                <CustomDropdown
                                    label="Department"
                                    list={dropdownList?.departmentlist || []}
                                    displayKey="department_name"
                                    selectedItem={selectedDept}
                                    setSelectedItem={setSelectedDept}
                                />

                                {/* Designation */}
                                <CustomDropdown
                                    label="Designation"
                                    list={dropdownList?.designationList || []}
                                    displayKey="designation_name"
                                    selectedItem={selectedDesig}
                                    setSelectedItem={setSelectedDesig}
                                />
                            </div>
                        </div>
                    </Card>
                    <Card className="p-2">
                        {/* earnings */}
                        <ComponentTable
                            type="Earnings"
                            components={earningComponents}
                            setComponents={setEarningComponents}
                            monthlyCtc={formData.monthly_ctc}
                            componentList={dropdownList.earningComponents}
                            earningComponents={earningComponents}
                        />
                    </Card>

                    <Card className="p-2">
                        {/* deductions */}
                        <ComponentTable
                            type="Deductions"
                            components={deductionComponents}
                            setComponents={setDeductionComponents}
                            monthlyCtc={formData.monthly_ctc}
                            componentList={dropdownList.deductionComponents}
                            earningComponents={earningComponents}
                        />
                    </Card>

                </div>

                {/* Column 2: One card spanning the full height */}
                <div className="sticky top-4 self-start">
                    <Card>
                        <h3 className="font-medium text-[15px]">Salary Preview</h3>
                        <span className="text-[12px] text-gray-400 mb-3 block">Real-time salary breakdown preview</span>

                        {/* Monthly CTC Input */}
                        <div className="mb-2">
                            <label className="text-[12px] font-medium text-gray-700">Monthly CTC</label>
                            <input
                                type="number"
                                value={monthlyCTC}
                                onChange={(e) => handleMonthlyCTCChange(e.target.value)}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-[12px] focus:outline-none focus:ring-1"
                                placeholder="Enter Monthly CTC"
                            />
                        </div>

                        <div className=' flex justify-between items-center mb-3 text-gray-400'>
                            <span className=' flex gap-2 items-center'>
                                <label className="text-[12px] font-medium">Monthly Gross</label>
                                <Tooltip title={<span className=' text-gray-100 text-xs'>
                                    Gross = CTC − Employer Contributions (PF + ESIC + Others)
                                </span>
                                }>
                                    <ExclamationCircleOutlined className="text-gray-400 hover:text-gray-600 cursor-pointer" />
                                </Tooltip>
                            </span>
                            <span>
                                {monthlyCTC - totalEmployerDeductions || 0}
                            </span>
                        </div>

                        {/* Annual CTC Input */}
                        <div className="mb-4">
                            <label className="text-[12px] font-medium text-gray-700">Annual CTC</label>
                            <input
                                type="number"
                                value={annualCTC}
                                readOnly
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-[12px] bg-gray-100"
                                placeholder="Annual CTC"
                            />
                        </div>

                        <hr className="my-3" />

                        <h4 className="text-xs font-semibold text-gray-600 mb-2">Salary Summary</h4>

                        <div className="text-[12px] space-y-3 border-t border-gray-200 pt-4 mt-4">
                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">Basic Pay:</span>
                                <span className="font-medium text-gray-800">₹ {basicPay}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">Total Earnings:</span>
                                <span className="font-medium text-green-700">₹ {totalEarnings}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-gray-600">Total Deductions:</span>
                                <span className="font-medium text-red-600">₹ {totalDeductions}</span>
                            </div>

                            <div className="flex justify-between items-center border-t border-gray-200 pt-3 mt-3">
                                <span className="font-semibold text-gray-800">Net Pay (Monthly):</span>
                                <span className="font-bold text-gray-700">₹ {netPayMonthly}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-800">Net Pay (Annual):</span>
                                <span className="font-bold text-gray-700">₹ {netPayAnnual}</span>
                            </div>
                        </div>
                    </Card>
                    <Card className='h-full mt-2'>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleInitalCancelBtn}
                                loading={loadings}
                                disabled={loadings}
                                className="bg-[#334b49] hover:bg-[#334b49]/80 text-white py-1 px-3 rounded-md"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleSubmit(false)}
                                disabled={!isFormValid && loadings}
                                className={`py-1 px-3 rounded-md text-white transition 
                                    ${loadings || formValid()
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-[#04B7B1] hover:bg-[#04B7B1]/80'}
                                `}
                            >
                                {loadings ? (
                                    <span className="flex items-center gap-2">
                                        <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10"
                                                stroke="currentColor" strokeWidth="4" fill="none" />
                                            <path className="opacity-75" fill="currentColor"
                                                d="M4 12a8 8 0 018-8v8z" />
                                        </svg>
                                        <span>Processing...</span>
                                    </span>
                                ) : fromEdit ? "Update Template" : "Save Template"}
                            </button>
                        </div>
                    </Card>
                </div>
            </div>


            {/* cancel pop up */}
            {isCancelClk ? (
                <CommonCancelPopup
                    handleCancelBtn={handleCancelBtn}
                    handleDraftBtn={handleSaveAsDraft}
                    testID='hs'
                />
            ) : null}

        </div>
    );
}

export default NewAddEditSalaryTemplate;